// Scrollable transcript. Two layout tricks make the "pin user message to
// top on send" UX work:
//
//   1. `containerType: 'size'` on the scroll viewport turns it into a CSS
//      query container, so descendants can reference its height via the
//      `cqh` unit instead of falling back to the document viewport.
//
//   2. `paddingBottom: 100cqh` while we're awaiting a reply reserves one
//      viewport of empty space below the last message — without it the
//      scroll container has nothing to scroll into past the bottom edge
//      and the new user bubble sits flush instead of pinning to the top.
//      Standard ChatGPT / Claude pattern. The padding collapses to `auto`
//      once the assistant has responded so settled conversations don't
//      leave a viewport-tall void below the last bubble.
//
// The scroll container fills the full ThreadContainer width; the inner
// column applies the configured max-width so messages stay centered while
// the scrollbar still hugs the right edge of the panel.

import { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { ArrowDown } from '@kula-ai/icons-core';
import { Message } from '../Message';
import { SpinnerMessage } from '../SpinnerMessage';
import {
  ConnectionPill,
  QueuedMessageStrip,
  TurnErrorStrip,
  UploadProgressStrip,
} from '../ThreadNotices';
import { ThreadBodySkeleton } from '../ThreadBodySkeleton';
import { isInProgress, isUserMessage } from '../../utils/messageUtils';
import { toolProgressLabel, workingStatusAt } from '../../../helpers';
import type { ThreadBodyProps } from './ThreadBody.types';

export const ThreadBody = ({
  messages,
  scrollRef,
  bodyContainerProps,
  isAwaitingReply,
  isInitialLoading,
  streamingSubSteps,
}: ThreadBodyProps) => {
  // Show the "Thinking…" card while we're waiting and the last message is
  // the user's. Once text starts streaming the draft becomes the last
  // message, so this turns off automatically.
  const lastMessage = messages[messages.length - 1];
  const showThinking =
    isAwaitingReply && !!lastMessage && isUserMessage(lastMessage);

  // Once the draft renders (cards/text on screen) the Thinking card turns
  // off — but the turn may still be running (final response composing,
  // more tools queued). Without a signal that window reads as "everything
  // is frozen": inputs are disabled and nothing moves. Show a compact
  // working strip naming the current step (e.g. "Composing response…"),
  // falling back to a generic label between steps.
  const showWorkingStrip = isAwaitingReply && !showThinking;
  // Humanise via the shared tool-label mapping — the substep's raw `name` is
  // a tool token ("kula_search") and must never reach the strip.
  const activeStep = [...(streamingSubSteps ?? [])]
    .reverse()
    .find((s) => s.status === 'active');
  const activeStepLabel = activeStep
    ? toolProgressLabel({
        name: activeStep.name,
        description: activeStep.description,
      })
    : undefined;
  // Between steps there's no tool label — show the rotating Claude-style
  // status instead of a frozen "Still working…". State only changes inside
  // the interval callback (never synchronously in the effect body — the
  // set-state-in-effect lint), so the first second may show the previous
  // word; the rotation is decorative, that's fine.
  const [stripTick, setStripTick] = useState(0);
  useEffect(() => {
    if (!showWorkingStrip) return undefined;
    const startedAt = Date.now();
    const t = setInterval(
      () => setStripTick(Math.floor((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => clearInterval(t);
  }, [showWorkingStrip]);
  const stripElapsed = showWorkingStrip ? stripTick : 0;

  // Jump-to-latest: the send UX pins the user's question to the TOP, so a
  // long streamed reply grows below the fold — and scrolling up mid-stream
  // used to leave no way back to the live tail but manual scrolling.
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;
    const measure = () =>
      setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
    const onScroll = () => {
      if (atBottomRafRef.current !== null) return;
      atBottomRafRef.current = requestAnimationFrame(() => {
        atBottomRafRef.current = null;
        measure();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Initial measurement — deferred a frame (no sync setState in effect).
    atBottomRafRef.current = requestAnimationFrame(() => {
      atBottomRafRef.current = null;
      measure();
    });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (atBottomRafRef.current !== null)
        cancelAnimationFrame(atBottomRafRef.current);
    };
  }, [scrollRef]);
  const jumpToLatest = () => {
    const el = scrollRef?.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  return (
    <Box pos="relative" flex="1" minH={0} display="flex" flexDirection="column">
      {/* Fade the scroll edges into the white surface so content dissolves
          under the header / composer instead of hard-cutting. */}
      <Box
        aria-hidden
        pos="absolute"
        top={0}
        left={0}
        right={0}
        h="24px"
        bgGradient="linear(to-b, base-white, transparent)"
        pointerEvents="none"
        zIndex={1}
      />
      <Box
        aria-hidden
        data-test-id="thread-body-bottom-fade"
        pos="absolute"
        bottom={0}
        left={0}
        right={0}
        h="32px"
        bgGradient="linear(to-t, base-white, transparent)"
        pointerEvents="none"
        zIndex={1}
      />
      <Box
        ref={scrollRef}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        flex="1"
        minH={0}
        overflowY="auto"
        px="16px"
        py="16px"
        sx={{ containerType: 'size' }}
      >
        <Box mx="auto" {...bodyContainerProps}>
          {isInitialLoading ? (
            <ThreadBodySkeleton />
          ) : (
            <Flex
              direction="column"
              gap="20px"
              sx={{ paddingBottom: isAwaitingReply ? '100cqh' : '32px' }}
            >
              {messages.map((message) => (
                <Box key={message.id} data-message-id={message.id}>
                  {isInProgress(message) ? (
                    <SpinnerMessage detail={message.content || undefined} />
                  ) : (
                    <Message message={message} />
                  )}
                </Box>
              ))}
              {showThinking ? (
                <Box data-test-id="thread-thinking">
                  <SpinnerMessage
                    startedAt={lastMessage.created_at}
                    subSteps={streamingSubSteps}
                  />
                </Box>
              ) : null}
              <TurnErrorStrip />
              <QueuedMessageStrip />
              <UploadProgressStrip />
              <ConnectionPill />
              {showWorkingStrip ? (
                <Flex
                  align="center"
                  gap="8px"
                  data-test-id="thread-working-strip"
                >
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="primary-500"
                    className="animate-pulse"
                    flexShrink={0}
                  />
                  <Text variant="small" className="text-secondary-500">
                    {`${activeStepLabel ?? workingStatusAt(stripElapsed)}…`}
                  </Text>
                </Flex>
              ) : null}
            </Flex>
          )}
        </Box>
      </Box>
      {!atBottom && (
        <Box
          as="button"
          type="button"
          onClick={jumpToLatest}
          position="absolute"
          bottom="12px"
          left="50%"
          transform="translateX(-50%)"
          display="inline-flex"
          alignItems="center"
          gap="4px"
          px="10px"
          py="6px"
          borderRadius="full"
          bg="base-white"
          border="1px solid"
          borderColor="secondary-200"
          boxShadow="0 2px 8px rgba(0,0,0,0.08)"
          color="secondary-700"
          fontSize="12px"
          fontWeight="500"
          cursor="pointer"
          _hover={{ bg: 'secondary-50' }}
          aria-label="Jump to latest"
          data-test-id="thread-jump-to-latest"
        >
          <ArrowDown size={12} color="currentColor" />
          Latest
        </Box>
      )}
    </Box>
  );
};
